import { imin, mulBp, withinRange } from '../math/fixed.ts';
import { rngRange } from '../math/rng.ts';
import type { BattleState, Hero } from '../types.ts';
import type { Pt } from '../caza-types.ts';
import type { MouthsRules, MouthsState } from '../roca-types.ts';
import { ev, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { archBlow } from './roca-layout.ts';
import { allyStrike, blowHorn, rocaNpc } from './roca-ally.ts';
import { ROCA_HORN } from './climb.ts';

/**
 * The mouths of the rock (docs/sendas/barbaro.md, chapter 5, act 2, the puzzle: «Las bocas de la roca»; §4): the cave's
 * hall with four mouths down to the sea, each under an arch of rock, cracked. A tap sends the hero to an arch: one blow,
 * one crack; with three it comes down over its mouth and cracks every standing arch joined to it by a crack in the roof
 * (and those, falling, theirs). At each mouth's entrance the tracks say what is below: the Wolf's (it crosses by the
 * stones and never drinks) where there is deep water, only the pack's at the one with stones below: that one is worth
 * keeping open (the Wolf can't heal there). Won with two mouths shut and nothing come down a moment; what is kept (which
 * are shut, whether the one without tracks stands) changes the fight. Cubs come up out of the open mouths.
 *
 * Cost: four arches; the cascade a few steps; the pack as any band.
 */

const mr = (s: BattleState): MouthsRules | undefined => s.rules?.senda?.roca?.mouths;
const ms = (s: BattleState): MouthsState | undefined => s.senda?.roca?.mouths;

const fresh = (r: MouthsRules): MouthsState => ({
  cracks: r.cracks.slice(),
  shut: r.cracks.map(() => 0),
  job: -1,
  t0: -1,
  last: -1,
  next: r.cubs.first,
  wolfNext: r.wolves.first,
  turn: 0,
  strikes: 0,
  falls: 0,
  horn: r.horn.uses,
  scared: 0,
  ullaCd: 0,
  bareLost: false,
  bareAt: -1,
  done: -1,
});

/** How many mouths are shut. */
export const mouthsShut = (w: MouthsState): number => w.shut.reduce((a, b) => a + b, 0);

/** A cub up out of a mouth (or in from the cave's back): a wolf, small and frail, its bite light. */
export const spawnCub = (s: BattleState, x: number, y: number, hp: number, dmgBp: number): void => {
  const e = spawnEnemy(s, 'wolf', 1, false, 0, x, y, hp);
  e.damage = mulBp(e.damage, dmgBp);
};

/** The hero's tap: an arch (to strike it once) or the horn. */
export const mouthsOrder = (s: BattleState, h: Hero, at: number, cue: (c: string) => void): void => {
  const r = mr(s);
  const w = ms(s);
  if (!r || !w || w.done >= 0) return;
  if (at === ROCA_HORN) {
    if (w.horn <= 0) return;
    w.horn--;
    blowHorn(s, rocaNpc(s, 'ulla'), r.horn);
    cue('horn');
    return;
  }
  if (at < 0 || at >= r.arches.length || w.shut[at]) return;
  w.job = at;
  w.t0 = -1;
  const p = r.arches[at] as Pt;
  orderMove(s, h, p.x, p.y);
};

/** Another order of the hero's own: the blow it was sent to strike is off. */
export const mouthsOff = (s: BattleState): void => {
  const w = ms(s);
  if (w) {
    w.job = -1;
    w.t0 = -1;
  }
};

/**
 * The mouth without tracks down: the pack's lair under it caved in, and the pack out through the roof's cracks at once,
 * at the rim nearest Ulla (whom it goes for), its wolves and its cubs.
 */
const surge = (s: BattleState, r: MouthsRules): void => {
  const u = rocaNpc(s, 'ulla');
  const ux = u?.x ?? 0;
  const uy = u?.y ?? 0;
  let at = r.rim[0] as Pt;
  let bd = -1;
  for (const p of r.rim) {
    const dx = (p.x - ux) >> 4;
    const dy = (p.y - uy) >> 4;
    const d = dx * dx + dy * dy;
    if (bd < 0 || d < bd) {
      bd = d;
      at = p;
    }
  }
  // (Between the rim and Ulla: out of the cracks onto the floor.)
  const x = at.x + ((ux - at.x) >> 2);
  const y = at.y + ((uy - at.y) >> 2);
  for (let i = 0; i < r.surge.wolves; i++) spawnEnemy(s, 'wolf', 1, false, 0, x + ((i % 3) - 1) * 520, y + (i >> 1) * 420, r.wolfHp);
  for (let i = 0; i < r.surge.cubs; i++) spawnCub(s, x + ((i % 3) - 1) * 380, y - 400 - (i >> 1) * 340, r.cubHp, r.cubDmgBp);
  ev(s, { t: 'senda', kind: 'rSurge', npc: '', x, y, n: r.trace });
};

/** One blow on an arch: its crack, and what comes down with it. */
const blow = (s: BattleState, r: MouthsRules, w: MouthsState, i: number, cue: (c: string) => void): void => {
  const after = archBlow(w.cracks, r.links, i, r.breaks);
  w.strikes++;
  const p = r.arches[i] as Pt;
  ev(s, { t: 'senda', kind: 'rStrike', npc: '', x: p.x, y: p.y, n: i });
  cue(`crack${w.strikes}`);
  for (let k = 0; k < after.length; k++) {
    const was = w.cracks[k] as number;
    const now = after[k] as number;
    if (now === was) continue;
    const a = r.arches[k] as Pt;
    ev(s, { t: 'senda', kind: 'rCrack', npc: '', x: a.x, y: a.y, n: k });
    if (now >= r.breaks && was < r.breaks) {
      w.shut[k] = 1;
      w.falls++;
      w.last = s.tick;
      const m = r.mouths[k] as Pt;
      ev(s, { t: 'senda', kind: 'rFall', npc: '', x: m.x, y: m.y, n: k });
      cue(`fall${w.falls}`);
      if (k === r.trace && !w.bareLost) {
        w.bareLost = true;
        w.bareAt = s.tick;
        surge(s, r);
        cue('bareLost');
      }
      if (mouthsShut(w) === r.need) cue('two');
    }
  }
  w.cracks = after;
};

export const mouthsTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = mr(s);
  const st = s.senda;
  if (!r || !st) return;
  const rs = (st.roca ??= {});
  const w = (rs.mouths ??= fresh(r));
  if (!s.started || w.done >= 0) return;
  const h = s.heroes.find((x) => x.downTicks === 0);
  // At the arch it was sent to: a moment there, and the blow.
  if (h && w.job >= 0) {
    const p = r.arches[w.job] as Pt;
    if (w.shut[w.job]) w.job = -1;
    else if (withinRange(h.x, h.y, p.x, p.y, r.reach) && h.stun === 0) {
      if (w.t0 < 0) w.t0 = s.tick;
      else if (s.tick - w.t0 >= r.strike) {
        const k = w.job;
        w.job = -1;
        w.t0 = -1;
        blow(s, r, w, k, cue);
      }
    } else w.t0 = -1;
  }
  // Settled: enough shut, nothing come down a while, nothing being struck (never once the pack's lair is down).
  if (!w.bareLost && mouthsShut(w) >= r.need && w.job < 0 && s.tick - w.last >= r.settle) {
    w.done = s.tick;
    let bits = 0;
    w.shut.forEach((v, k) => {
      if (v) bits |= 1 << k;
    });
    st.nums.shut = bits;
    st.nums.bare = w.shut[r.trace] ? 0 : r.trace + 1;
    cue('settled');
    return;
  }
  // Cubs up out of the open mouths, by turns; in from the back with none open.
  if (s.waveT >= w.next) {
    w.next = s.waveT + (w.bareLost ? r.surge.every : r.cubs.every);
    const open = w.shut.map((v, k) => (v ? -1 : k)).filter((k) => k >= 0);
    const n = rngRange(s.rng, r.cubs.n[0], r.cubs.n[1]);
    const p = open.length ? (r.mouths[open[w.turn % open.length] as number] as Pt) : (r.rim[w.turn % r.rim.length] as Pt);
    w.turn++;
    // (Out of the hole, onto its lip toward the hall's middle.)
    const x = p.x - (p.x > 0 ? 700 : -700);
    const y = p.y - (p.y > -400 ? 700 : -700);
    for (let i = 0; i < n; i++) spawnCub(s, x + ((i % 3) - 1) * 380, y + (i >> 1) * 340, r.cubHp, r.cubDmgBp);
    cue('cubs1');
  }
  if (s.waveT >= w.wolfNext) {
    w.wolfNext = s.waveT + r.wolves.every;
    const p = r.rim[(w.turn + 1) % r.rim.length] as Pt;
    for (let i = 0; i < r.wolves.n; i++) spawnEnemy(s, 'wolf', 1, false, 0, p.x + i * 500, p.y, r.wolfHp);
  }
  w.ullaCd = allyStrike(s, rocaNpc(s, 'ulla'), r.ulla, w.ullaCd);
};

/** Mouths shut, of the two the act needs. */
export const mouthsGoal = (s: BattleState): { done: number; need: number } => {
  const r = mr(s);
  const w = ms(s);
  const need = r?.need ?? 2;
  return { done: w ? imin(need, mouthsShut(w)) : 0, need };
};

export const mouthsResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = mr(s);
  const w = ms(s);
  // The mouth without tracks down: the pack out of its lair, and the attempt lost a moment after (the surge seen).
  if (r && w && w.bareAt >= 0 && s.tick >= w.bareAt + r.surge.lost) return 'lost';
  return w && w.done >= 0 && s.tick >= w.done + 12 ? 'won' : null;
};
